import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserCircle, FloppyDisk, Lock } from "@phosphor-icons/react";

const initials = (name) => (name || "").split(" ").map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
const inputCls = "bg-surface-2 border-border text-fg";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      display_name: user.display_name || user.name || "",
      job_title: user.job_title || "",
      department: user.department || "",
      phone: user.phone || "",
      mobile: user.mobile || "",
      preferred_language: user.preferred_language || "fr",
    });
    setDirty(false);
  }, [user]);

  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  if (!form) return <div className="text-muted text-sm animate-pulse">Chargement…</div>;

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/profile", form);
      await refresh();
      setDirty(false);
      toast.success("Profil mis à jour ✅");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="profile-page" className="max-w-2xl">
      <h1 className="font-display text-3xl font-bold">Mon profil</h1>
      <p className="text-muted text-sm mt-1 mb-6">Votre identité personnelle. Le nom affiché est utilisé dans l'interface et comme nom d'expéditeur par défaut.</p>

      <div className="surface rounded-lg p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center font-display font-bold text-accent text-xl" data-testid="profile-avatar">
            {initials(form.display_name || `${form.first_name} ${form.last_name}`)}
          </div>
          <div>
            <div className="font-display font-bold text-fg text-lg">{form.display_name || `${form.first_name} ${form.last_name}`.trim() || "—"}</div>
            <div className="text-xs text-muted">{form.job_title || "Fonction non renseignée"}</div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Prénom"><Input className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="profile-first-name" /></Field>
          <Field label="Nom"><Input className={inputCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="profile-last-name" /></Field>
          <Field label="Nom affiché" hint="Ce que voient vos interlocuteurs">
            <Input className={inputCls} value={form.display_name} onChange={(e) => set("display_name", e.target.value)} placeholder="Ahmed Omari" data-testid="profile-display-name" />
          </Field>
          <Field label="Fonction"><Input className={inputCls} value={form.job_title} onChange={(e) => set("job_title", e.target.value)} placeholder="Directeur" data-testid="profile-job-title" /></Field>
          <Field label="Téléphone professionnel"><Input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+33 1 23 45 67 89" data-testid="profile-phone" /></Field>
          <Field label="Mobile (optionnel)"><Input className={inputCls} value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder="+33 6 …" data-testid="profile-mobile" /></Field>
          <Field label="Langue préférée">
            <Select value={form.preferred_language} onValueChange={(v) => set("preferred_language", v)}>
              <SelectTrigger className={inputCls} data-testid="profile-language"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="fr">Français</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
            </Select>
          </Field>
        </div>

        <div className="mt-5 pt-4 border-t border-border">
          <div className="overline mb-1.5 flex items-center gap-1.5"><Lock size={12} /> Adresse de connexion</div>
          <div className="flex items-center gap-2">
            <Input className={`${inputCls} opacity-70`} value={user?.email || ""} disabled data-testid="profile-login-email" />
          </div>
          <p className="text-xs text-muted mt-1.5">L'adresse de connexion n'est pas modifiable pour le moment. Votre adresse d'<b>envoi</b> se règle dans « Email & Signature ».</p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button onClick={save} disabled={!dirty || saving} data-testid="profile-save"
          className="flex items-center gap-2 bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift disabled:opacity-50">
          <FloppyDisk size={16} weight="bold" /> {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
        {dirty && <span className="text-xs text-[#F5A623]" data-testid="profile-unsaved">Modifications non enregistrées</span>}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-muted mt-1">{hint}</p>}
    </div>
  );
}
